import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { hook, withValue } from '@g4/automation-hooks';

import { formatStringForHookValue } from '../../../utils/automationHookUtils';

import ExpandableMenuItem from './navGroup/ExpandableMenuItem';
import NavItem from './navGroup/NavItem';

const propTypes = {
  forceExpanded: PropTypes.bool,
  items: PropTypes.array,
  displayAsColumn: PropTypes.bool,
  displayAsDropdown: PropTypes.bool,
  defaultBackgroundType: PropTypes.oneOf(['light', 'dark']),
  automationHook: PropTypes.string,
};

const defaultProps = {
  items: [],
  displayAsColumn: false,
  displayAsDropdown: false,
  defaultBackgroundType: 'light',
};

const NavContainer = styled.ul`
  display: flex;
  flex-direction: ${props => (props.displayAsColumn ? 'column' : 'row')};
  align-items: ${props => (props.displayAsColumn ? 'flex-start' : 'flex-end')};
  justify-content: space-between;

  ${props =>
    props.displayAsDropdown &&
    `
    background: ${props.theme.colors.siteHeader.light.linkBackground};
  `}
`;

const NavListItem = styled.li`
  display: flex;
  height: 100%;
`;

const GroupMenuItem = ({ children }) => {
  return children({ displayAsColumn: true });
};

const NavGroup = ({
  items,
  displayAsColumn,
  displayAsDropdown,
  defaultBackgroundType,
  forceExpanded,
  automationHook,
}) => {
  const getMenuItem = (item, automationHook) => {
    if (item.__typename === 'ExpandableMenuItem') {
      return (
        <ExpandableMenuItem
          item={item}
          defaultBackgroundType={defaultBackgroundType}
          forceExpanded={forceExpanded}
          automationHook={withValue(
            `${automationHook}-item`,
            formatStringForHookValue(item.title)
          )}
        >
          {props =>
            item.items && item.items.length ? (
              <NavGroup
                items={item.items}
                automationHook={automationHook}
                {...props}
              />
            ) : null
          }
        </ExpandableMenuItem>
      );
    }

    if (item.__typename === 'GroupMenuItem') {
      return (
        <GroupMenuItem item={item}>
          {props =>
            item.items && item.items.length ? (
              <NavGroup
                items={item.items}
                automationHook={automationHook}
                {...props}
                forceExpanded={true}
              />
            ) : null
          }
        </GroupMenuItem>
      );
    }

    return (
      <NavItem
        item={item}
        onclick={item.onclick}
        defaultBackgroundType={defaultBackgroundType}
        automationHook={withValue(
          `${automationHook}-item`,
          formatStringForHookValue(item.title)
        )}
      />
    );
  };

  return (
    <NavContainer
      displayAsColumn={displayAsColumn}
      displayAsDropdown={displayAsDropdown}
      {...hook(automationHook)}
    >
      {items.map(item => (
        <NavListItem key={item.id}>
          {getMenuItem(item, automationHook)}
        </NavListItem>
      ))}
    </NavContainer>
  );
};

NavGroup.propTypes = propTypes;
NavGroup.defaultProps = defaultProps;

export default NavGroup;
